import { reactive } from "vue";
import $ls, { LocalStorageKey } from "@/utils/local-storage";

/**
 * Position and size of the floating canvas panels, kept per panel id so a panel
 * reopens exactly where the user left it.
 *
 * Module-level reactive store, like the other canvas states (DraggerMenuState…),
 * persisted to localStorage: on tablet the default panel size rarely fits, and
 * having to resize the same panel on every session was a recurring complaint.
 * FloatingPanel re-clamps whatever is restored, so a geometry saved on another
 * window size can never land off-screen.
 */
export interface PanelGeometry {
  /** Viewport coordinates of the panel's top-left corner. */
  left: number;
  top: number;
  width: number;
  height: number;
}

function isValidGeometry(value: unknown): value is PanelGeometry {
  if (typeof value !== "object" || value === null) return false;
  const geo = value as Record<string, unknown>;
  return (["left", "top", "width", "height"] as const).every((field) =>
    Number.isFinite(geo[field]),
  );
}

/** Stored geometries, filtered: a corrupt entry must never produce NaN clamping. */
function loadStoredGeometries(): Record<string, PanelGeometry> {
  try {
    const raw = $ls.getItem(LocalStorageKey.FLOATING_PANEL_GEOMETRIES);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, geometry]) => isValidGeometry(geometry)),
    ) as Record<string, PanelGeometry>;
  } catch {
    return {};
  }
}

const geometries = reactive<Record<string, PanelGeometry>>(
  loadStoredGeometries(),
);

/** Stored geometry for a panel, or null when it has never been opened/moved. */
export function getPanelGeometry(id: string): PanelGeometry | null {
  return geometries[id] ?? null;
}

export function setPanelGeometry(id: string, geometry: PanelGeometry): void {
  geometries[id] = { ...geometry };
  try {
    $ls.setItem(
      LocalStorageKey.FLOATING_PANEL_GEOMETRIES,
      JSON.stringify(geometries),
    );
  } catch {
    // Quota/private mode: the in-memory value still holds for the session.
  }
}

export default geometries;
